import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table } from '@/components/ui';
import { useConstructionScopes } from './hooks/useScopes';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { ScopeStatus } from './types/construction-scope.types';

const statusColors: Record<ScopeStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  pending_review: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-600',
  locked: 'bg-slate-900 text-white',
  revision_required: 'bg-amber-100 text-amber-600',
  superseded: 'bg-slate-400 text-white',
  cancelled: 'bg-red-100 text-red-600',
};

const statusLabels: Record<ScopeStatus, string> = {
  draft: 'Bản thảo',
  pending_review: 'Chờ duyệt',
  approved: 'Đã duyệt',
  locked: 'Đã khóa',
  revision_required: 'Cần chỉnh sửa',
  superseded: 'Đã thay thế',
  cancelled: 'Đã hủy',
};

export default function ScopeListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    scopes,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useConstructionScopes(companyId);

  return (
    <PageLayout
      title="Phạm vi Thi công (Construction Scope)"
      description="Quản lý chi tiết các hạng mục thi công, định vị khu vực và giai đoạn triển khai thực địa."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Phạm vi' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-history-line">Lịch sử phiên bản</Button>
          <AuthButton
            module="construction"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Tạo phạm vi mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng số Scope"
            value={stats?.totalScopes || 0}
            icon="ri-bubble-chart-line"
            variant="primary"
          />
          <KPICard
            title="Đã phê duyệt"
            value={stats?.approvedScopes || 0}
            icon="ri-checkbox-circle-line"
            variant="success"
          />
          <KPICard
            title="Tổng số hạng mục"
            value={stats?.totalItems || 0}
            icon="ri-list-settings-line"
            variant="info"
          />
          <KPICard
            title="Hạng mục/Scope TB"
            value={Math.round(stats?.avgItemsPerScope || 0)}
            icon="ri-organization-chart"
            variant="warning"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã Scope, tên hạng mục..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã Scope' },
              { key: 'name', title: 'Tên phạm vi & Hợp đồng' },
              { key: 'dates', title: 'Thời gian thi công' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
              { key: 'actions', title: '', align: 'right' },
            ]}
            data={scopes}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary  tracking-wider">{item.scope_code}</span>;
                case 'name':
                  return (
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900 line-clamp-1">{item.scope_name}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-500 font-bold ">{item.contract?.contract_code || 'Chưa gán HĐ'}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400 font-medium">{item.project?.name}</span>
                      </div>
                    </div>
                  );
                case 'dates':
                  return (
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-700">{item.start_date ? new Date(item.start_date).toLocaleDateString('vi-VN') : '---'}</span>
                      <span className="text-[10px] text-slate-400 italic">đến {item.end_date ? new Date(item.end_date).toLocaleDateString('vi-VN') : '---'}</span>
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                case 'actions':
                  return (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" icon="ri-eye-line" />
                      <Button variant="ghost" size="sm" icon="ri-edit-line" />
                    </div>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
